import React, { useEffect, useState } from 'react';
import API from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { Settings2, Mail, RefreshCw } from 'lucide-react';

export default function SettingsPage() {
  const [settings, setSettings] = useState({});
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState({});
  const [testEmail, setTestEmail] = useState('');
  const [testing, setTesting]   = useState(false);

  const load = async () => {
    setLoading(true);
    try { const r = await API.get('/settings'); setSettings(r.data); }
    catch(e) { toast.error('Failed to load settings'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async (key, value) => {
    setSaving(p => ({ ...p, [key]: true }));
    try {
      await API.put('/settings', { key, value });
      setSettings(p => ({ ...p, [key]: value }));
      toast.success('Setting saved');
    } catch(e) { toast.error('Save failed'); }
    finally { setSaving(p => ({ ...p, [key]: false })); }
  };

  const sendTestEmail = async () => {
    if (!testEmail) { toast.error('Enter an email address'); return; }
    setTesting(true);
    try {
      const r = await API.post('/notifications/test', null, { params: { to: testEmail } });
      if (r.data.sent) toast.success('Test email sent!');
      else toast.warning(`Email skipped (SMTP_ENABLED=${r.data.smtp_enabled}). Set SMTP_ENABLED=true in .env to send real emails.`);
    } catch(e) { toast.error('Test failed'); }
    finally { setTesting(false); }
  };

  return (
    <div className="p-6 space-y-6 max-w-2xl">
      <div>
        <h1 className="text-xl font-bold flex items-center gap-2"><Settings2 size={20} />System Settings</h1>
        <p className="text-sm text-slate-500">Configure thresholds, notifications, and export behaviour.</p>
      </div>

      {loading ? <div className="flex justify-center py-8"><RefreshCw size={20} className="animate-spin text-slate-400" /></div> : (
        <div className="space-y-4">

          {/* High-value threshold */}
          <div className="bg-white rounded-lg border shadow-sm p-5 space-y-3">
            <h3 className="text-sm font-semibold">Transaction Thresholds</h3>
            <div className="grid grid-cols-2 gap-4 items-end">
              <div>
                <Label className="text-xs">High-Value Threshold (ZAR)</Label>
                <Input
                  type="number"
                  defaultValue={settings.high_value_threshold}
                  onBlur={e => save('high_value_threshold', parseFloat(e.target.value))}
                  className="mt-1 h-8 text-sm"
                  data-testid="setting-high-value-threshold"
                />
                <p className="text-xs text-slate-400 mt-1">Transactions above this amount are automatically flagged for finance review.</p>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={!!settings.require_review_above_threshold}
                  onCheckedChange={v => save('require_review_above_threshold', v)}
                />
                <Label className="text-xs">Require finance review above threshold</Label>
              </div>
            </div>
          </div>

          {/* Export settings */}
          <div className="bg-white rounded-lg border shadow-sm p-5 space-y-3">
            <h3 className="text-sm font-semibold">Export Controls</h3>
            <div className="flex items-center gap-3">
              <Switch
                checked={settings.export_approved_only !== false}
                onCheckedChange={v => save('export_approved_only', v)}
              />
              <div>
                <Label className="text-xs font-medium">Export APPROVED transactions only</Label>
                <p className="text-xs text-slate-400">Recommended: prevents unapproved charges being posted to GL. Finance Admin can force-export with override.</p>
              </div>
            </div>
          </div>

          {/* SMTP / Notifications */}
          <div className="bg-white rounded-lg border shadow-sm p-5 space-y-3">
            <h3 className="text-sm font-semibold flex items-center gap-1.5"><Mail size={14} />SMTP Notifications</h3>
            <div className="bg-slate-50 rounded p-3 text-xs space-y-1 font-mono">
              <p className="font-semibold text-slate-600 mb-2 font-sans">Configure in <code>/app/backend/.env</code>:</p>
              {[['SMTP_ENABLED', 'true or false'], ['SMTP_HOST', 'localhost or mail.company.com'], ['SMTP_PORT', '25 or 587'], ['SMTP_USER', 'optional username'], ['SMTP_PASS', 'optional password'], ['SMTP_FROM', 'noreply@company.com'], ['SMTP_TLS', 'true or false']].map(([k, v]) => (
                <div key={k}><span className="text-blue-600">{k}</span><span className="text-slate-400">="</span><span className="text-emerald-600">{v}</span><span className="text-slate-400">"</span></div>
              ))}
            </div>
            <div className="flex gap-2 items-end">
              <div className="flex-1">
                <Label className="text-xs">Test email address</Label>
                <Input value={testEmail} onChange={e => setTestEmail(e.target.value)}
                  placeholder="you@company.com" className="mt-1 h-8 text-sm" />
              </div>
              <Button size="sm" variant="outline" onClick={sendTestEmail} disabled={testing}>
                {testing ? <RefreshCw size={13} className="animate-spin mr-1" /> : <Mail size={13} className="mr-1" />}
                Send Test
              </Button>
            </div>
          </div>

        </div>
      )}
    </div>
  );
}
